<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { tableIconName } from '../lib/icons';
  import { builtInTablesStore } from '../lib/custom-tables-store';
  import { onMount, onDestroy } from 'svelte';
  import { subscribeSchemaSSE } from '../lib/schema-sse';
  import AppShell from '../components/AppShell.svelte';
  import StatusBadge from '../components/StatusBadge.svelte';
  import Avatar from '../components/Avatar.svelte';
  import KpiCard from '../components/KpiCard.svelte';
  import {
    fetchPayments,
    fetchPaymentSummary,
    type PaymentRow,
    type PaymentSummary,
  } from '../lib/persons-api';
  import { fmtDateTime, fmtInt, fmtPct, fmtUah } from '../lib/format';
  import Pagination from '../components/Pagination.svelte';
  import { bulkDeletePayments, createPayment, fetchPaymentIds, fetchSavedFilters, type SavedFilter } from '../lib/admin-api';
  import { session } from '../lib/session';
  import { push } from 'svelte-spa-router';
  import { ApiError } from '../lib/api';
  import FilterBuilder from '../components/FilterBuilder.svelte';
  import {
    emptyGroup,
    countActive,
    fieldsForScope,
    apiFieldToFilterDef,
    type Group,
    type FieldDef,
  } from '../lib/filter-tree';
  import { fetchPublicDataTable, reorderDataFields, type ApiField } from '../lib/data-tables-api';
  import { updatePayment } from '../lib/admin-api';
  import FieldDisplay from '../lib/fields/FieldDisplay.svelte';
  import CellEdit from '../components/CellEdit.svelte';
  import BulkEditModal from '../components/BulkEditModal.svelte';
  import ColumnVisibilityMenu from '../components/ColumnVisibilityMenu.svelte';
  import ViewPresetsMenu from '../components/ViewPresetsMenu.svelte';
  import ExportHistoryButton from '../components/ExportHistoryButton.svelte';
  import { canViewField, type Role } from '../lib/roles';
  import type { ApiTableView, ApiRecord } from '../lib/data-tables-api';
  import { generateCsv, downloadCsv, columnsFromFields } from '../lib/csv-export';
  import { loadState, saveState } from '../lib/persisted-state';
  import ViewModeToggle from '../components/ViewModeToggle.svelte';
  import KanbanBoard from '../components/KanbanBoard.svelte';
  import KanbanCardConfigModal from '../components/KanbanCardConfigModal.svelte';
  import KanbanBulkBar from '../components/KanbanBulkBar.svelte';
  import { groupableFields as kanbanGroupableFields } from '../lib/kanban-grouping';
  import { fetchKanbanFacets, fetchKanbanColumnRaw, fetchKanbanColumnIds, type KanbanSource } from '../lib/kanban-api';
  import {
    loadCardConfig, saveCardConfig, loadViewMode, saveViewMode, loadColumnWidths, saveColumnWidths,
    type KanbanCardConfig, type KanbanGranularity,
  } from '../lib/kanban-config';

  type SortValue =
    | 'occurredDesc'
    | 'occurredAsc'
    | 'amountDesc'
    | 'amountAsc'
    | `custom:${string}:asc`
    | `custom:${string}:desc`;

  // Persisted UI-стан (відновлюється з sessionStorage). Дозволяє повернутись
  // на сторінку без втрати фільтрів/сортування/сторінки.
  interface PersistedPayments {
    search: string;
    rangeDays: number;
    status: string;
    unmatchedOnly: boolean;
    inferredOnly: boolean;
    minCents: number | null;
    productSku: string;
    sort: SortValue;
    page: number;
    pageSize: number;
    appliedTree: Group;
  }
  const persistedDefault: PersistedPayments = {
    search: '',
    rangeDays: 3650,
    status: '',
    unmatchedOnly: false,
    inferredOnly: false,
    minCents: null,
    productSku: '',
    sort: 'occurredDesc',
    page: 1,
    pageSize: 10,
    appliedTree: emptyGroup(),
  };
  const restored = loadState<PersistedPayments>('payments-list', persistedDefault);

  let items = $state<PaymentRow[]>([]);
  let summary = $state<PaymentSummary | null>(null);
  let total = $state(0);
  let loading = $state(true);
  let err = $state<string | null>(null);

  // Live search.
  let search = $state(restored.search);

  // Applied filters (restored з sessionStorage).
  let rangeDays = $state(restored.rangeDays);
  let status = $state(restored.status);
  let unmatchedOnly = $state(restored.unmatchedOnly);
  let inferredOnly = $state(restored.inferredOnly);
  let minCents = $state<number | null>(restored.minCents);
  let productSku = $state(restored.productSku);

  // Pagination state.
  let page = $state(restored.page);
  let pageSize = $state(restored.pageSize);

  // Sort state.
  let sort = $state<SortValue>(restored.sort);

  // Tree-based filter (FilterBuilder).
  let appliedTree = $state<Group>(restored.appliedTree);
  let draftTree = $state<Group>(emptyGroup());
  let showBuilder = $state(false);
  let savedFilters = $state<SavedFilter[]>([]);
  const treeFilterCount = $derived(countActive(appliedTree));

  // Auto-persist при будь-якій зміні фільтрів/сортування/пагінації.
  $effect(() => {
    saveState<PersistedPayments>('payments-list', {
      search,
      rangeDays,
      status,
      unmatchedOnly,
      inferredOnly,
      minCents,
      productSku,
      sort,
      page,
      pageSize,
      appliedTree,
    });
  });

  // Custom-поля для filter-builder (з налаштувань "Структура даних → payments").
  let customFilterFields = $state<FieldDef[]>([]);
  /** Усі поля catalog (built-in + custom) для schema-driven LIST. */
  let allFields = $state<ApiField[]>([]);
  /** Мета таблиці (редаговані назва/опис/колір/іконка з налаштувань).
   *  Беремо з кешованого стору → одразу без блимання при перемиканні. */
  const tableMeta = $derived($builtInTablesStore['payments']);
  const tableName = $derived(tableMeta?.name ?? 'Платежі');
  const tableDesc = $derived(tableMeta?.description ?? '');
  const tableColor = $derived(tableMeta?.color ?? '#10b981');
  const tableIcon = $derived(tableMeta?.icon ?? 'payments');
  const filterFields = $derived<FieldDef[]>([...fieldsForScope('payments'), ...customFilterFields]);

  const currentRole = $derived(($session?.role ?? null) as Role | null);

  /** In-memory override від view-пресету (null = використовує DB show_in_list). */
  let columnOverride = $state<string[] | null>(null);

  /** Видимі колонки (built-in + custom) — sort just by position.
   *  Built-in catalog default = -10000+i → природно першими. Admin може
   *  mixed-reorder через DnD у LIST header. */
  const tableColumns = $derived<ApiField[]>(
    columnOverride
      ? (columnOverride
          .map((key) => allFields.find((f) => f.fieldKey === key))
          .filter(Boolean)
          .filter((f) => canViewField(currentRole, f!.min_view_role)) as ApiField[])
      : allFields
          .filter(
            (f) =>
              !f.hidden &&
              f.show_in_list !== false &&
              canViewField(currentRole, f.min_view_role),
          )
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  );

  function applyViewPreset(v: ApiTableView) {
    if (v.columns.length > 0) columnOverride = v.columns;
    if (v.sortKey) sort = v.sortKey as SortValue;
    if (v.filters) appliedTree = v.filters as Group;
    page = 1;
    void load();
  }

  function sortKeyForPayment(fieldKey: string): 'occurred' | 'amount' | null {
    if (fieldKey === 'occurredAt') return 'occurred';
    if (fieldKey === 'amountCents') return 'amount';
    return null;
  }

  /** Bulk-edit modal: тільки custom-fields (built-in payments — webhook-sourced). */
  let bulkEditOpen = $state(false);
  const bulkEditableFields = $derived<ApiField[]>(
    allFields.filter(
      (f) =>
        !f.builtIn &&
        !f.is_computed &&
        !f.hidden &&
        f.type !== 'linked_record' &&
        f.type !== 'linked_summary',
    ),
  );
  async function applyBulkEditToOne(id: string, field: ApiField, value: unknown): Promise<void> {
    await updatePayment(id, { customFields: { [field.fieldKey]: value } });
  }
  const exportParams = $derived<Record<string, unknown>>({
    columns: tableColumns.map((f) => f.fieldKey),
    sort,
    filter: treeFilterCount > 0 ? JSON.stringify(appliedTree) : undefined,
    rangeDays,
    search: search || undefined,
    status: status || undefined,
    unmatchedOnly: unmatchedOnly || undefined,
    inferredOnly: inferredOnly || undefined,
    minCents: minCents ?? undefined,
    productSku: productSku || undefined,
  });

  async function onBulkEditDone(progress: { done: number; total: number; errors: number }) {
    confirmMsg = `Оновлено ${progress.done - progress.errors} з ${progress.total}${progress.errors > 0 ? ` (помилки: ${progress.errors})` : ''}`;
    setTimeout(() => (confirmMsg = null), 6000);
    bulkEditOpen = false;
    clearSelection();
    await load();
  }

  /* ── Kanban (картковий вид) ──────────────────────────────────────────────── */
  // Payment built-in поля read-only (webhook-sourced) → дошка read-only групування
  // (без drag-to-move); інлайн-редагування лише custom-полів.
  const vmRestored = loadViewMode('payments');
  let viewMode = $state<'list' | 'kanban'>(vmRestored?.viewMode ?? 'list');
  let kanbanFieldKey = $state<string | null>(vmRestored?.kanbanFieldKey ?? null);
  let granularity = $state<KanbanGranularity>(vmRestored?.granularity ?? 'month');
  let kanbanReload = $state(0);

  const groupableFields = $derived(
    kanbanGroupableFields(allFields.filter((f) => f.show_in_list !== false)),
  );
  const kanbanField = $derived(groupableFields.find((f) => f.fieldKey === kanbanFieldKey) ?? null);
  const kanbanFieldType = $derived(kanbanField?.type ?? 'single_line');
  const cardConfigFields = $derived(
    allFields.filter((f) => !f.hidden && f.type !== 'linked_summary' && f.type !== 'linked_record'),
  );

  let cardConfig = $state<KanbanCardConfig>({ primaryFieldKey: null, fields: [], cardSize: 'medium' });
  let cardConfigOpen = $state(false);

  /** Payment → плоский values для картки. provider/payer недоступні у списку. */
  function paymentToValues(p: PaymentRow): Record<string, unknown> {
    return {
      occurredAt: p.occurredAt,
      amountCents: p.amountCents,
      currency: p.currency,
      status: p.status,
      externalRef: p.externalRef,
      rawTariff: p.rawTariff,
      productSku: p.productSku,
      personId: p.person?.id,
      ...(p.customFields ?? {}),
    };
  }
  function paymentToRec(p: PaymentRow): ApiRecord {
    return { id: p.id, values: paymentToValues(p), createdAt: p.occurredAt, updatedAt: p.occurredAt };
  }

  /** Інлайн-редагування на картці — лише custom-поля (built-in read-only). */
  function canEditKanban(f: ApiField): boolean {
    return !f.builtIn && !f.is_computed;
  }
  // Payment built-in поля read-only → дошка read-only групування (без drag).
  const canDragKanban = false;

  function kanbanParams(): URLSearchParams {
    const p = new URLSearchParams();
    p.set('groupField', kanbanFieldKey ?? '');
    if (kanbanFieldType === 'date' || kanbanFieldType === 'datetime') p.set('granularity', granularity);
    p.set('rangeDays', String(rangeDays));
    if (search) p.set('search', search);
    if (status) p.set('status', status);
    if (unmatchedOnly) p.set('unmatchedOnly', 'true');
    if (inferredOnly) p.set('inferredOnly', 'true');
    if (minCents != null) p.set('minCents', String(minCents));
    if (productSku) p.set('productSku', productSku);
    if (treeFilterCount > 0) p.set('filter', JSON.stringify(appliedTree));
    p.set('sort', sort);
    return p;
  }
  const kanbanSource: KanbanSource = {
    loadFacets: (cp) => fetchKanbanFacets('/payments/kanban', kanbanParams(), cp),
    loadCards: async (k, pg) => {
      const r = await fetchKanbanColumnRaw<PaymentRow>('/payments/kanban', kanbanParams(), k, pg);
      return { records: r.items.map(paymentToRec), total: r.total };
    },
    loadIds: async (k) => (await fetchKanbanColumnIds('/payments/kanban', kanbanParams(), k)).ids,
  };

  function bumpKanban() { kanbanReload += 1; }

  async function onKanbanCellEdit(recordId: string, field: ApiField, value: unknown) {
    if (field.builtIn) return;
    await updatePayment(recordId, { customFields: { [field.fieldKey]: value } });
  }

  function onSwitchMode(m: 'list' | 'kanban') {
    viewMode = m;
    if (m === 'kanban') {
      cardConfig = loadCardConfig('payments') ?? cardConfig;
      bumpKanban();
    }
  }
  function saveCard(c: KanbanCardConfig) {
    cardConfig = c;
    saveCardConfig('payments', c);
    cardConfigOpen = false;
  }
  let kanbanRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleKanbanRefresh() {
    if (viewMode !== 'kanban') return;
    if (kanbanRefreshTimer) clearTimeout(kanbanRefreshTimer);
    kanbanRefreshTimer = setTimeout(() => { kanbanRefreshTimer = null; bumpKanban(); }, 600);
  }
  // Persist view-mode per table.
  $effect(() => {
    saveViewMode('payments', { viewMode, kanbanFieldKey, granularity });
  });

  /** Inline cell-edit для custom columns у Payments LIST (як у Persons). */
  /** Чи редагується клітинка (тільки custom-поля; built-in приходять з webhook). */
  function isPaymentCellEditable(col: ApiField): boolean {
    if (!isAdmin || col.builtIn) return false;
    if (col.is_computed || col.type === 'formula' || col.type === 'stat' || col.type === 'linked_summary') {
      return false;
    }
    return true;
  }

  /** Зберегти одне custom-поле платежу (popover-редактор). Кидає помилку — CellEdit покаже alert. */
  async function persistPaymentCell(paymentId: string, col: ApiField, value: unknown) {
    const v = value === '' || value === undefined ? null : value;
    await updatePayment(paymentId, { customFields: { [col.fieldKey]: v } });
    const idx = items.findIndex((it) => it.id === paymentId);
    if (idx >= 0) {
      const next = { ...items[idx]! };
      const cf = { ...(next.customFields ?? {}) };
      if (v === null) delete cf[col.fieldKey];
      else cf[col.fieldKey] = v;
      next.customFields = cf;
      items[idx] = next;
      items = [...items];
    }
  }

  async function loadSavedFilters() {
    try {
      savedFilters = await fetchSavedFilters('payments');
    } catch {
      savedFilters = [];
    }
  }
  function openBuilder() {
    draftTree = JSON.parse(JSON.stringify(appliedTree)) as Group;
    showBuilder = true;
    void loadSavedFilters();
  }
  function applyBuilder() {
    appliedTree = draftTree;
    showBuilder = false;
    page = 1;
    void load();
  }
  function cancelBuilder() {
    showBuilder = false;
  }
  function clearBuilder() {
    draftTree = emptyGroup();
  }

  // Selection state (multi-select + bulk delete).
  let selected = $state<Set<string>>(new Set());

  /** KPI-картки: якщо вибрані рядки — рахуємо по них (клієнтом, із завантажених
   *  рядків поточної вибірки); інакше — серверний summary (фільтр / усі). */
  const displaySummary = $derived.by<PaymentSummary | null>(() => {
    if (selected.size === 0) return summary;
    const rows = items.filter((p) => selected.has(p.id));
    if (rows.length === 0) return summary;
    let grossCents = 0, refundCents = 0, approved = 0;
    for (const p of rows) {
      if (p.status === 'REFUNDED') refundCents += p.amountCents;
      else { grossCents += p.amountCents; approved++; }
    }
    const netCents = grossCents - refundCents;
    return {
      rangeDays: summary?.rangeDays ?? 0,
      grossCents,
      refundCents,
      netCents,
      feesCents: 0,
      netAfterFeesCents: netCents,
      aovCents: approved > 0 ? Math.round(grossCents / approved) : 0,
      count: approved,
      unmatchedCount: 0,
    };
  });

  let confirmOpen = $state(false);
  let confirmBusy = $state(false);
  let confirmMsg = $state<string | null>(null);

  // Split-button + create modal.
  let actionsMenuOpen = $state(false);
  let createOpen = $state(false);
  let createBusy = $state(false);
  let createErr = $state<string | null>(null);
  let nAmount = $state('');
  let nCurrency = $state('UAH');
  let nStatus = $state<'SUCCEEDED' | 'REFUNDED' | 'FAILED' | 'PENDING'>('SUCCEEDED');
  let nOccurredAt = $state(new Date().toISOString().slice(0, 10));
  let nExternalRef = $state('');
  let nProvider = $state<'WAYFORPAY' | 'LIQPAY' | 'FONDY' | 'PORTMONE' | 'STRIPE' | 'UNKNOWN'>('UNKNOWN');
  let nPayerEmail = $state('');
  let nPayerPhone = $state('');
  let nRawTariff = $state('');

  function resetCreateForm() {
    nAmount = '';
    nCurrency = 'UAH';
    nStatus = 'SUCCEEDED';
    nOccurredAt = new Date().toISOString().slice(0, 10);
    nExternalRef = '';
    nProvider = 'UNKNOWN';
    nPayerEmail = '';
    nPayerPhone = '';
    nRawTariff = '';
    createErr = null;
  }
  async function submitCreatePayment(e: SubmitEvent) {
    e.preventDefault();
    const amount = Number(nAmount);
    if (!amount || amount <= 0) {
      createErr = 'Введіть суму більше 0';
      return;
    }
    if (!nPayerEmail.trim() && !nPayerPhone.trim()) {
      createErr = "Потрібен email або телефон платника";
      return;
    }
    createBusy = true;
    createErr = null;
    try {
      const r = await createPayment({
        amountUah: amount,
        currency: nCurrency.trim() || 'UAH',
        status: nStatus,
        occurredAt: nOccurredAt,
        externalRef: nExternalRef.trim() || undefined,
        provider: nProvider,
        payerEmail: nPayerEmail.trim() || undefined,
        payerPhone: nPayerPhone.trim() || undefined,
        rawTariff: nRawTariff.trim() || undefined,
      });
      createOpen = false;
      resetCreateForm();
      push(`/payments/${r.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'payment.duplicate') {
        const d = (e.details as { matchedId?: string } | undefined) ?? {};
        const link = d.matchedId ? `\n\n→ #/payments/${d.matchedId}` : '';
        createErr = `${e.message}${link}`;
      } else {
        createErr = e instanceof Error ? e.message : String(e);
      }
    } finally {
      createBusy = false;
    }
  }

  const isAdmin = $derived($session?.role === 'ADMIN');
  const allOnPageSelected = $derived(
    items.length > 0 && items.every((i) => selected.has(i.id)),
  );

  function toggleRow(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    selected = next;
  }
  function togglePage() {
    const next = new Set(selected);
    if (allOnPageSelected) for (const i of items) next.delete(i.id);
    else for (const i of items) next.add(i.id);
    selected = next;
  }
  function clearSelection() {
    selected = new Set();
  }
  async function selectAllUnderFilter() {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    p.set('rangeDays', String(rangeDays));
    if (search) p.set('search', search);
    if (unmatchedOnly) p.set('unmatchedOnly', 'true');
    if (inferredOnly) p.set('inferredOnly', 'true');
    if (minCents !== null) p.set('minCents', String(minCents));
    if (productSku) p.set('productSku', productSku);
    if (treeFilterCount > 0) p.set('filter', JSON.stringify(appliedTree));
    const r = await fetchPaymentIds(p.toString());
    const next = new Set(selected);
    for (const id of r.ids) next.add(id);
    selected = next;
  }
  async function doDelete() {
    confirmBusy = true;
    confirmMsg = null;
    try {
      const ids = Array.from(selected);
      const batches: string[][] = [];
      for (let i = 0; i < ids.length; i += 1000) batches.push(ids.slice(i, i + 1000));
      let totalDeleted = 0;
      for (const batch of batches) {
        const r = await bulkDeletePayments(batch);
        totalDeleted += r.deleted;
      }
      clearSelection();
      confirmOpen = false;
      page = 1;
      await load();
      confirmMsg = `Видалено ${totalDeleted} платежів.`;
      setTimeout(() => (confirmMsg = null), 4000);
    } catch (e) {
      confirmMsg = e instanceof Error ? e.message : String(e);
    } finally {
      confirmBusy = false;
    }
  }

  // Draft filters (in popover).
  let draftRangeDays = $state(3650);
  let draftStatus = $state('');
  let draftUnmatched = $state(false);
  let draftInferred = $state(false);
  let draftMinUah = $state<string>(''); // user types гривні; конвертуємо при apply
  let draftProductSku = $state('');

  let showFilters = $state(false);

  const activeFilterCount = $derived(
    (status ? 1 : 0) +
      (rangeDays !== 3650 ? 1 : 0) +
      (unmatchedOnly ? 1 : 0) +
      (inferredOnly ? 1 : 0) +
      (minCents !== null ? 1 : 0) +
      (productSku ? 1 : 0),
  );

  async function load() {
    loading = true;
    err = null;
    try {
      // Спільні фільтри для списку і summary — KPI картки (Gross/Refunds/
      // Net/AOV) перераховуються по тих самих рядках що в таблиці.
      const sharedFilters = {
        rangeDays,
        search: search || undefined,
        status: status || undefined,
        unmatchedOnly: unmatchedOnly || undefined,
        inferredOnly: inferredOnly || undefined,
        minCents: minCents ?? undefined,
        productSku: productSku || undefined,
        filter: treeFilterCount > 0 ? JSON.stringify(appliedTree) : undefined,
      };
      const [p, s] = await Promise.all([
        fetchPayments({ ...sharedFilters, sort, page, pageSize }),
        fetchPaymentSummary(sharedFilters),
      ]);
      items = p.items;
      total = p.total;
      summary = s;
      if (viewMode === 'kanban') bumpKanban();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  async function reloadSchema() {
    try {
      const schema = await fetchPublicDataTable('payments');
      allFields = schema.fields ?? [];
      const customFields = allFields.filter((f) => !f.builtIn);
      customFilterFields = customFields.map(apiFieldToFilterDef);
    } catch {
      allFields = [];
      customFilterFields = [];
    }
  }

  let unsubSchemaSSE: (() => void) | null = null;

  onMount(async () => {
    await reloadSchema();
    if (viewMode === 'kanban') { cardConfig = loadCardConfig('payments') ?? cardConfig; bumpKanban(); }
    await load();
    unsubSchemaSSE = subscribeSchemaSSE(
      'payments',
      () => void reloadSchema(),
      () => scheduleKanbanRefresh(),
    );
  });

  onDestroy(() => unsubSchemaSSE?.());

  // Live-search debounce.
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  function onSearchInput() {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      page = 1;
      void load();
    }, 300);
  }

  function onPageChange(p: number) {
    page = p;
    void load();
  }
  function onPageSizeChange(s: number) {
    pageSize = s;
    page = 1;
    void load();
  }
  function toggleSort(column: 'occurred' | 'amount') {
    if (suppressSortClick) return;
    const desc = `${column}Desc` as SortValue;
    const asc = `${column}Asc` as SortValue;
    sort = sort === desc ? asc : sort === asc ? desc : desc;
    page = 1;
    void load();
  }
  function toggleSortCustom(fieldKey: string) {
    if (suppressSortClick) return;
    const desc = `custom:${fieldKey}:desc` as SortValue;
    const asc = `custom:${fieldKey}:asc` as SortValue;
    sort = sort === desc ? asc : sort === asc ? desc : desc;
    page = 1;
    void load();
  }
  function sortIconCustom(fieldKey: string): string {
    if (sort === `custom:${fieldKey}:desc`) return '▼';
    if (sort === `custom:${fieldKey}:asc`) return '▲';
    return '↕';
  }

  const SORTABLE_FIELD_TYPES = new Set([
    'number', 'datetime', 'date', 'currency', 'auto_number', 'percent',
    'checkbox', 'formula', 'linked_summary', 'stat',
  ]);
  function isSortableType(type: string): boolean {
    return SORTABLE_FIELD_TYPES.has(type);
  }

  /** Drag&drop reorder колонок (HTML5 native). */
  let draggedColId = $state<string | null>(null);
  let dropTargetColId = $state<string | null>(null);

  // ── Resize колонок (table-fixed, ширина зберігається у localStorage) ──
  const COL_MIN = 80, COL_MAX = 600;
  let colWidths = $state<Record<string, number>>(loadColumnWidths('payments', '__list__'));
  function defColW(col: ApiField): number {
    if (colWidths[col.fieldKey]) return colWidths[col.fieldKey];
    const t = col.type, k = col.fieldKey;
    if (k === 'externalRef') return 220;
    if (t === 'email') return 220;
    if (t === 'multi_line') return 240;
    if (t === 'datetime' || t === 'date') return 150;
    if (t === 'number' || t === 'currency' || t === 'percent' || t === 'auto_number' || t === 'stat') return 120;
    if (t === 'checkbox') return 90;
    return 160;
  }
  function colStyle(key: string): string {
    const col = tableColumns.find((c) => c.fieldKey === key);
    return `width:${col ? defColW(col) : 160}px`;
  }
  const totalColW = $derived((isAdmin ? 44 : 0) + tableColumns.reduce((s, c) => s + defColW(c), 0));
  let colResizing: { key: string; startX: number; startW: number } | null = null;
  /** Блокує sort-клік на заголовку одразу після ресайзу. */
  let suppressSortClick = false;
  function startColResize(e: PointerEvent, key: string) {
    e.preventDefault();
    e.stopPropagation();
    suppressSortClick = true;
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    const startW = colWidths[key] ?? th.getBoundingClientRect().width;
    colResizing = { key, startX: e.clientX, startW };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onColResizeMove(e: PointerEvent) {
    if (!colResizing) return;
    const w = Math.max(COL_MIN, Math.min(COL_MAX, colResizing.startW + (e.clientX - colResizing.startX)));
    colWidths = { ...colWidths, [colResizing.key]: w };
  }
  function endColResize() {
    if (!colResizing) return;
    saveColumnWidths('payments', '__list__', colWidths);
    colResizing = null;
    setTimeout(() => { suppressSortClick = false; }, 250);
  }
  function onColDragStart(e: DragEvent, fieldId: string) {
    if (!e.dataTransfer) return;
    draggedColId = fieldId;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldId);
  }
  function onColDragOver(e: DragEvent, fieldId: string) {
    if (!draggedColId || draggedColId === fieldId) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    dropTargetColId = fieldId;
  }
  function onColDragLeave() {
    dropTargetColId = null;
  }
  async function onColDrop(e: DragEvent, targetFieldId: string) {
    e.preventDefault();
    const sourceId = draggedColId;
    draggedColId = null;
    dropTargetColId = null;
    if (!sourceId || sourceId === targetFieldId) return;
    const cols = [...tableColumns];
    const fromIdx = cols.findIndex((c) => c.id === sourceId);
    const toIdx = cols.findIndex((c) => c.id === targetFieldId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = cols.splice(fromIdx, 1);
    cols.splice(toIdx, 0, moved!);
    try {
      await reorderDataFields(
        'payments',
        cols.map((c, i) => ({ id: c.id, position: i, groupName: c.groupName ?? null })),
      );
      const schema = await fetchPublicDataTable('payments');
      allFields = schema.fields ?? [];
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
    }
  }
  function sortIcon(column: 'occurred' | 'amount'): string {
    if (sort === `${column}Desc`) return '▼';
    if (sort === `${column}Asc`) return '▲';
    return '↕';
  }

  function openFilters() {
    draftRangeDays = rangeDays;
    draftStatus = status;
    draftUnmatched = unmatchedOnly;
    draftInferred = inferredOnly;
    draftMinUah = minCents != null ? String(Math.round(minCents / 100)) : '';
    draftProductSku = productSku;
    showFilters = true;
  }
  function applyFilters() {
    rangeDays = Number(draftRangeDays);
    status = draftStatus;
    unmatchedOnly = draftUnmatched;
    inferredOnly = draftInferred;
    const minUahNum = Number(draftMinUah);
    minCents = draftMinUah && Number.isFinite(minUahNum) ? Math.round(minUahNum * 100) : null;
    productSku = draftProductSku.trim();
    showFilters = false;
    page = 1;
    void load();
  }
  function resetFiltersInDraft() {
    draftRangeDays = 3650;
    draftStatus = '';
    draftUnmatched = false;
    draftInferred = false;
    draftMinUah = '';
    draftProductSku = '';
  }
  function clearAllFilters() {
    rangeDays = 3650;
    status = '';
    unmatchedOnly = false;
    inferredOnly = false;
    minCents = null;
    productSku = '';
    resetFiltersInDraft();
    showFilters = false;
    page = 1;
    void load();
  }
</script>

<svelte:window onpointermove={onColResizeMove} onpointerup={endColResize} />

<AppShell active="payments">
  <div class="mx-auto max-w-[1200px]">
    <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
      <div class="flex items-center gap-3">
        <div class="grid h-11 w-11 shrink-0 place-items-center rounded-full text-white" style="background: {tableColor}">
          <Icon name={tableIconName(tableIcon)} size={20} />
        </div>
        <div>
          <h1 class="text-xl font-semibold leading-tight">
            {tableName} <span class="text-sm text-[var(--color-text-muted)] font-medium ml-1">· {fmtInt(total)}</span>
          </h1>
          <p class="mt-0.5 text-[12px] text-[var(--color-text-muted)] min-h-[16px]">{tableDesc}</p>
        </div>
      </div>
      {#if isAdmin}
        <div class="flex items-center gap-2">
          <a
            href="#/settings/data-structure/tables/payments"
            class="rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)] inline-flex items-center gap-1.5"
          >
            <Icon name="settings" size={14} /> Налаштування
          </a>
          <div class="relative inline-flex">
            <button
              onclick={() => { actionsMenuOpen = false; resetCreateForm(); createOpen = true; }}
              class="rounded-l-md btn-pu px-3 py-1.5 text-xs font-semibold"
            >
              + Новий запис
            </button>
            <button
              onclick={() => (actionsMenuOpen = !actionsMenuOpen)}
              aria-label="Більше дій"
              class="inline-flex items-center rounded-r-md btn-pu border-l border-white/30 px-2 py-1.5 text-xs"
            >
              <Icon name="chevronDown" size={15} />
            </button>
            {#if actionsMenuOpen}
              <button
                type="button"
                onclick={() => (actionsMenuOpen = false)}
                class="fixed inset-0 z-20 cursor-default"
                aria-label="Close menu"
              ></button>
              <div class="absolute right-0 top-full mt-1 z-30 min-w-[200px] rounded-md border border-[var(--color-border-3)] bg-white shadow-lg overflow-hidden">
                <button
                  type="button"
                  onclick={() => { actionsMenuOpen = false; resetCreateForm(); createOpen = true; }}
                  class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-[var(--color-bg-3)]"
                >
                  <Icon name="add" size={14} /><span>Новий запис</span>
                </button>
                <a
                  href="#/settings/data-structure/import/payments"
                  onclick={() => (actionsMenuOpen = false)}
                  class="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[var(--color-bg-3)] no-underline text-[var(--color-text)]"
                >
                  <Icon name="download" size={14} /><span>Імпорт з файлу</span>
                </a>
              </div>
            {/if}
          </div>
        </div>
      {/if}
    </header>

    <!-- Toolbar: два рядки -->
    <div class="mb-4 space-y-2">
      <!-- Рядок 1: Пошук + Фільтр + CSV -->
      <div class="relative flex flex-wrap items-center gap-2">
        <div class="w-full sm:flex-1 relative">
          <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted-2)] flex"><Icon name="search" size={14} /></span>
          <input
            bind:value={search}
            oninput={onSearchInput}
            placeholder="Пошук у Платежі"
            class="w-full rounded-md border border-[var(--color-border-3)] bg-white pl-8 pr-2.5 py-1.5 text-xs"
          />
        </div>
        <button
          onclick={openBuilder}
          class="relative inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors
            {treeFilterCount > 0
              ? 'border-[var(--color-pu)] bg-purple-50 text-[var(--color-pu-3)]'
              : 'border-[var(--color-border-3)] bg-white text-[var(--color-text)] hover:bg-[var(--color-bg-3)]'}"
        >
          <Icon name="filter" size={14} /> Фільтр
          {#if treeFilterCount > 0}
            <span class="ml-1 inline-block rounded-full bg-[var(--color-pu)] text-white text-[9.5px] font-bold px-1.5 py-0.5 leading-none">
              {treeFilterCount}
            </span>
          {/if}
        </button>
        {#if treeFilterCount > 0}
          <button onclick={() => { appliedTree = emptyGroup(); page = 1; void load(); }} class="text-[10.5px] text-[var(--color-text-muted)] hover:underline">
            скинути всі
          </button>
        {/if}
        <ExportHistoryButton
          tableSlug="payments"
          label="Платежі · {new Date().toLocaleDateString('uk-UA')}"
          totalCount={total}
          params={exportParams}
          offerAllFields
        />
        <!-- Filter panel (absolute, всередині relative row) -->
        {#if showFilters}
          <div class="absolute left-0 top-full mt-2 z-20 w-[360px] rounded-xl border border-[var(--color-border-3)] bg-white p-4 shadow-xl">
            <div class="text-[11px] font-bold uppercase tracking-[1px] text-[var(--color-text-muted-2)] mb-3">
              Фільтр платежів
            </div>
            <div class="space-y-3">
              <label class="block">
                <span class="block text-[10.5px] font-semibold mb-1">Статус</span>
                <select bind:value={draftStatus} class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs">
                  <option value="">Усі статуси</option>
                  <option value="SUCCEEDED">SUCCEEDED</option>
                  <option value="REFUNDED">REFUNDED</option>
                  <option value="FAILED">FAILED</option>
                  <option value="PENDING">PENDING</option>
                </select>
              </label>
              <label class="block">
                <span class="block text-[10.5px] font-semibold mb-1">Період</span>
                <select bind:value={draftRangeDays} class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs">
                  <option value={30}>Останні 30 днів</option>
                  <option value={90}>Останні 90 днів</option>
                  <option value={365}>Останній рік</option>
                  <option value={3650}>Усі дані</option>
                </select>
              </label>
              <div class="grid grid-cols-2 gap-2">
                <label class="block">
                  <span class="block text-[10.5px] font-semibold mb-1">Мін. сума, ₴</span>
                  <input bind:value={draftMinUah} type="number" min="0" placeholder="напр. 1000" class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                </label>
                <label class="block">
                  <span class="block text-[10.5px] font-semibold mb-1">Product SKU</span>
                  <input bind:value={draftProductSku} placeholder="club-monthly…" class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs" />
                </label>
              </div>
              <label class="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" bind:checked={draftUnmatched} />
                <span>Тільки без зіставлення з Product</span>
              </label>
              <label class="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" bind:checked={draftInferred} />
                <span>Тільки inferred (синтезовані з SP)</span>
              </label>
            </div>
            <div class="mt-4 flex items-center justify-between border-t border-[var(--color-border-3)] pt-3">
              <button onclick={resetFiltersInDraft} class="text-[11px] text-[var(--color-text-muted)] hover:underline">Очистити</button>
              <div class="flex gap-2">
                <button onclick={() => (showFilters = false)} class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-[11px] font-medium">Скасувати</button>
                <button onclick={applyFilters} class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-[11px] font-semibold text-white">Застосувати</button>
              </div>
            </div>
          </div>
        {/if}
      </div>

      <!-- Рядок 2: Вид + Колонки + Порядок + Views -->
      <div class="flex flex-wrap items-center gap-2">
        <ViewModeToggle
          mode={viewMode}
          {kanbanFieldKey}
          {groupableFields}
          {granularity}
          onModeChange={onSwitchMode}
          onKanbanFieldChange={(k) => { kanbanFieldKey = k; if (viewMode === 'kanban') bumpKanban(); }}
          onGranularityChange={(g) => { granularity = g; if (viewMode === 'kanban') bumpKanban(); }}
        />
        <ColumnVisibilityMenu
          slug="payments"
          fields={allFields.filter((f) => canViewField(currentRole, f.min_view_role))}
          onChanged={async () => {
            try {
              const schema = await fetchPublicDataTable('payments');
              allFields = schema.fields ?? [];
            } catch {}
          }}
        />
        <ViewPresetsMenu
          slug="payments"
          currentColumns={tableColumns.map((f) => f.fieldKey)}
          currentSortKey={sort}
          currentFilters={treeFilterCount > 0 ? appliedTree : null}
          onApply={applyViewPreset}
          {isAdmin}
        />
      </div>
    </div>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else}
      {#if displaySummary}
        {@const feePct = displaySummary.grossCents > 0 ? Math.round((displaySummary.feesCents / displaySummary.grossCents) * 1000) / 10 : 0}
        {#if selected.size > 0}
          <div class="mb-2 text-[11px] font-semibold text-[var(--color-pu-3)]">KPI по вибраних: {fmtInt(selected.size)}</div>
        {/if}
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <KpiCard eyebrow="Gross" value={fmtUah(displaySummary.grossCents)} />
          <KpiCard eyebrow="Refunds" value={fmtUah(displaySummary.refundCents)} />

          <!-- Net картка з hover-tooltip про комісії -->
          <div class="relative group cursor-help">
            <KpiCard eyebrow="Net" value={fmtUah(displaySummary.netCents)} />
            <!-- маленький індикатор що є tooltip -->
            <span class="absolute top-2 right-2 grid h-4 w-4 place-items-center rounded-full bg-[var(--color-pu-light)] text-[9px] font-bold text-[var(--color-pu-3)] opacity-60 group-hover:opacity-100 transition-opacity">i</span>
            <!-- tooltip -->
            <div class="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 w-[280px] rounded-xl border border-[var(--color-border-3)] bg-white p-3.5 shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity">
              <div class="text-[10px] font-semibold uppercase tracking-[1px] text-[var(--color-text-muted-2)] mb-2.5">
                Розклад чистого доходу
              </div>
              <div class="space-y-1.5 text-xs">
                <div class="flex justify-between">
                  <span class="text-[var(--color-text-muted)]">Gross</span>
                  <strong class="tabular-nums">{fmtUah(displaySummary.grossCents)}</strong>
                </div>
                <div class="flex justify-between text-red-600">
                  <span>− Refunds</span>
                  <strong class="tabular-nums">{fmtUah(displaySummary.refundCents)}</strong>
                </div>
                <div class="flex justify-between pt-1 mt-1 border-t border-dashed border-[var(--color-border-3)]">
                  <span class="text-[var(--color-text-muted)]">Net (без комісії)</span>
                  <strong class="tabular-nums">{fmtUah(displaySummary.netCents)}</strong>
                </div>
                <div class="flex justify-between text-amber-700">
                  <span>− Комісія процесора <span class="text-[10px]">({fmtPct(feePct)})</span></span>
                  <strong class="tabular-nums">{fmtUah(displaySummary.feesCents)}</strong>
                </div>
                <div class="flex justify-between pt-2 mt-2 border-t border-[var(--color-border-3)] text-emerald-700">
                  <span class="font-bold">= Чистий дохід</span>
                  <strong class="tabular-nums text-[13px]">{fmtUah(displaySummary.netAfterFeesCents)}</strong>
                </div>
              </div>
              <!-- стрілочка вгору -->
              <div class="absolute -top-1.5 left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 border-l border-t border-[var(--color-border-3)] bg-white"></div>
            </div>
          </div>

          <KpiCard eyebrow="AOV" value={fmtUah(displaySummary.aovCents)} valueSub="· {fmtInt(displaySummary.count)} транз." />
        </div>
        {#if displaySummary.unmatchedCount > 0}
          <div class="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800">
            <Icon name="warning" size={14} class="shrink-0" /> {fmtInt(displaySummary.unmatchedCount)} платежів без зіставлення з Product. Налаштуйте matchers у Settings → Продукти.
          </div>
        {/if}
      {/if}

      <!-- Bulk action bar (list-view; для kanban — плаваюча KanbanBulkBar) -->
      {#if viewMode === 'list' && isAdmin && selected.size > 0}
        <div class="mb-2 flex items-center gap-3 rounded-lg border border-[var(--color-pu)]/40 bg-purple-50 px-3.5 py-2 text-xs">
          <span class="font-semibold">Вибрано: {selected.size}</span>
          {#if selected.size < total}
            <button onclick={selectAllUnderFilter} class="text-[var(--color-pu-3)] hover:underline">
              Вибрати все ({fmtInt(total)})
            </button>
          {/if}
          <button onclick={clearSelection} class="text-[var(--color-text-muted)] hover:underline ml-auto">
            скинути
          </button>
          {#if bulkEditableFields.length > 0}
            <button
              onclick={() => (bulkEditOpen = true)}
              class="inline-flex items-center gap-1.5 rounded-md btn-pu px-3 py-1.5 text-[11px] font-semibold"
            ><Icon name="edit" size={13} /> Редагувати</button>
          {/if}
          <button
            onclick={() => { confirmOpen = true; confirmMsg = null; }}
            class="inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white px-3 py-1.5 text-[11px] font-semibold hover:bg-red-700"
          ><Icon name="delete" size={13} /> Видалити</button>
        </div>
      {/if}
      {#if confirmMsg && !confirmOpen}
        <div class="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-700">
          {confirmMsg}
        </div>
      {/if}

      {#if viewMode === 'kanban' && kanbanFieldKey}
        <KanbanBoard
          slug="payments"
          groupFieldKey={kanbanFieldKey}
          groupFieldType={kanbanFieldType}
          source={kanbanSource}
          reloadToken={kanbanReload}
          canDragGroup={canDragKanban}
          fields={cardConfigFields}
          config={cardConfig}
          onConfigClick={() => (cardConfigOpen = true)}
          onRecordClick={(id) => push(`/payments/${id}`)}
          onCellEdit={isAdmin ? onKanbanCellEdit : undefined}
          canEdit={canEditKanban}
          selectable={isAdmin}
          selectedIds={selected}
          onSelectionChange={(s) => (selected = s)}
        />
        <KanbanBulkBar
          count={selected.size}
          onEdit={bulkEditableFields.length > 0 ? () => (bulkEditOpen = true) : undefined}
          onDelete={() => { confirmOpen = true; confirmMsg = null; }}
          onClear={clearSelection}
        />
      {:else}

      <div class="overflow-hidden rounded-2xl border border-[var(--color-border-2)] bg-[var(--color-surface)] shadow-card">
        <div class="overflow-x-auto">
        <table class="table-fixed text-[13px] [&_td]:overflow-hidden [&_th]:overflow-hidden" style="width: {totalColW}px;">
          <thead class="sticky top-0 z-[1] glass">
            <tr class="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-text-muted-2)] border-b border-[var(--color-border-2)]">
              {#if isAdmin}
                <th class="text-left px-3 py-2 w-[30px]">
                  <input type="checkbox" checked={allOnPageSelected}
                         onclick={(e) => { e.stopPropagation(); togglePage(); }} />
                </th>
              {/if}
              {#each tableColumns as col (col.id)}
                {@const sortK = sortKeyForPayment(col.fieldKey)}
                {@const isDropTarget = dropTargetColId === col.id}
                {#if sortK}
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-[var(--color-bg-2)] {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    onclick={() => toggleSort(sortK)}
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="Клік — sort · drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label} <span class="text-[var(--color-pu-3)]">{sortIcon(sortK)}</span></span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {:else if !col.builtIn && isSortableType(col.type)}
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold cursor-pointer select-none hover:bg-[var(--color-bg-2)] {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    onclick={() => toggleSortCustom(col.fieldKey)}
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="Клік — sort · drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label} <span class="text-[var(--color-pu-3)]">{sortIconCustom(col.fieldKey)}</span></span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {:else if !col.builtIn}
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold select-none {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label}</span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {:else}
                  <th
                    style={colStyle(col.fieldKey)} class="relative group/col text-left px-3 py-2 font-bold {isDropTarget ? 'bg-[var(--color-pu)]/10 outline-2 outline-dashed outline-[var(--color-pu)]' : ''}"
                    ondragover={(e) => onColDragOver(e, col.id)}
                    ondragleave={onColDragLeave}
                    ondrop={(e) => onColDrop(e, col.id)}
                    title="drag — перемістити колонку"
                  >
                    {#if isAdmin}<span draggable="true" ondragstart={(e) => onColDragStart(e, col.id)} onclick={(e) => e.stopPropagation()} class="absolute left-1.5 top-1/2 -translate-y-1/2 inline-flex text-[var(--color-text-muted-2)] cursor-grab opacity-0 group-hover/col:opacity-100 transition-opacity z-[1]" title="Перетягнути колонку"><Icon name="grip" size={13} /></span>{/if}
<span class="block truncate align-middle transition-transform duration-150 group-hover/col:translate-x-[15px]">{col.label}</span>
                    <div class="col-resize" role="separator" aria-orientation="vertical" onpointerdown={(e) => startColResize(e, col.fieldKey)}></div>
                  </th>
                {/if}
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each items as p (p.id)}
              <tr class="border-b border-[var(--color-border-2)] hover:bg-[var(--color-pu)]/[0.04] cursor-pointer transition-colors duration-150"
                  class:selected-row={selected.has(p.id)}
                  onclick={() => (window.location.hash = `#/payments/${p.id}`)}>
                {#if isAdmin}
                  <td class="px-3 py-2.5" onclick={(e) => { e.stopPropagation(); toggleRow(p.id); }}>
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onclick={(e) => { e.stopPropagation(); toggleRow(p.id); }}
                    />
                  </td>
                {/if}
                {#each tableColumns as col (col.id)}
                  {#if col.fieldKey === 'occurredAt'}
                    <td class="px-3 py-2.5 text-[11px] tabular-nums whitespace-nowrap">{fmtDateTime(p.occurredAt)}</td>
                  {:else if col.fieldKey === 'personId'}
                    <td class="px-3 py-2.5" onclick={(e) => { if (p.person) { e.stopPropagation(); window.location.hash = `#/persons/${p.person.id}`; } }}>
                      {#if p.person}
                        <div class="flex items-center gap-2.5 min-w-0">
                          <Avatar name={p.person.displayName} />
                          <div class="min-w-0">
                            <div class="font-semibold hover:underline truncate">{p.person.displayName ?? '—'}</div>
                            <div class="text-[10px] text-[var(--color-text-muted)] truncate">{p.person.primaryEmail ?? ''}</div>
                          </div>
                        </div>
                      {:else}
                        <span class="text-[var(--color-text-muted-2)] italic text-[11px]">контакт видалено</span>
                      {/if}
                    </td>
                  {:else if col.fieldKey === 'productSku'}
                    <td class="px-3 py-2.5 text-xs">
                      {#if p.productSku}
                        {p.productSku}
                      {:else}
                        <span class="text-[var(--color-text-muted)]">—</span>
                        <span class="ml-1 inline-block rounded-full bg-blue-100 px-1.5 py-0.5 text-[9.5px] font-bold text-blue-700">unmatched</span>
                      {/if}
                    </td>
                  {:else if col.fieldKey === 'rawTariff'}
                    <td class="px-3 py-2.5 font-mono text-[10.5px] text-[var(--color-text-muted)]">{p.rawTariff ?? '—'}</td>
                  {:else if col.fieldKey === 'amountCents'}
                    <td class="px-3 py-2.5 font-semibold tabular-nums {p.status === 'REFUNDED' ? 'text-red-600' : ''}">
                      {p.status === 'REFUNDED' ? '– ' : ''}{fmtUah(p.amountCents)}
                    </td>
                  {:else if col.fieldKey === 'status'}
                    <td class="px-3 py-2.5">
                      <StatusBadge status={p.status} />
                      {#if p.inferred}<span class="ml-1 inline-block rounded-full bg-blue-100 px-1.5 py-0.5 text-[9.5px] font-bold text-blue-700">inferred</span>{/if}
                    </td>
                  {:else if col.fieldKey === 'externalRef'}
                    <td class="px-3 py-2.5 font-mono text-[10.5px] text-[var(--color-text-muted)]">{p.externalRef ?? '—'}</td>
                  {:else if col.builtIn}
                    <td class="px-3 py-2.5 text-xs">
                      <FieldDisplay field={col} value={(p as unknown as Record<string, unknown>)[col.fieldKey]} />
                    </td>
                  {:else}
                    <td class="px-3 py-2.5 text-xs">
                      <CellEdit
                        field={col}
                        value={p.customFields?.[col.fieldKey]}
                        record={{ ...p, ...(p.customFields ?? {}) }}
                        editable={isPaymentCellEditable(col)}
                        onSave={(v) => persistPaymentCell(p.id, col, v)}
                      />
                    </td>
                  {/if}
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
        </div>
        <div class="border-t border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-3.5 py-2.5">
          <div class="text-[11px] text-[var(--color-text-muted)] mb-2">
            Показано <strong>{items.length}</strong> з <strong>{fmtInt(total)}</strong>
          </div>
          <Pagination {total} {page} {pageSize} onpage={onPageChange} onpagesize={onPageSizeChange} />
        </div>
      </div>
      {/if}
    {/if}
  </div>

  {#if cardConfigOpen}
    <KanbanCardConfigModal
      slug="payments"
      availableFields={cardConfigFields}
      config={cardConfig}
      sampleValues={items[0] ? paymentToValues(items[0]) : {}}
      onSave={saveCard}
      onClose={() => (cardConfigOpen = false)}
    />
  {/if}

  <BulkEditModal
    open={bulkEditOpen}
    selectedIds={Array.from(selected)}
    editableFields={bulkEditableFields}
    entityLabel={selected.size === 1 ? 'платежу' : 'платежів'}
    onApply={applyBulkEditToOne}
    onClose={() => (bulkEditOpen = false)}
    onDone={onBulkEditDone}
  />

  <FilterBuilder
    open={showBuilder}
    bind:root={draftTree}
    onApply={applyBuilder}
    onCancel={cancelBuilder}
    onClear={clearBuilder}
    savedFilters={savedFilters}
    onSavedChanged={loadSavedFilters}
    currentSort={sort}
    onSavedLoad={(sf) => {
      if (sf.sort && sf.sort !== sort) {
        sort = sf.sort as SortValue;
        page = 1;
        void load();
      }
    }}
    scope="payments"
    customFields={filterFields}
  />

  {#if confirmOpen}
    <div
      class="fixed inset-0 z-40 bg-black/40 flex items-center justify-center"
      onclick={() => !confirmBusy && (confirmOpen = false)}
      role="presentation"
    >
      <div
        class="w-[min(440px,90vw)] rounded-xl bg-white shadow-2xl p-5"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div class="flex items-center gap-2 text-sm font-bold mb-2"><Icon name="delete" size={16} /> Видалити платежів?</div>
        <div class="text-xs text-[var(--color-text-muted)] mb-4">
          Буде назавжди видалено <strong class="text-[var(--color-text)]">{selected.size}</strong>
          {selected.size === 1 ? 'платіж' : 'платежів'}. LTV відповідних контактів буде
          перераховано автоматично. Це незворотньо.
        </div>
        {#if confirmMsg}
          <div class="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">{confirmMsg}</div>
        {/if}
        <div class="flex justify-end gap-2">
          <button
            onclick={() => (confirmOpen = false)}
            disabled={confirmBusy}
            class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-xs font-medium"
          >Скасувати</button>
          <button
            onclick={doDelete}
            disabled={confirmBusy}
            class="rounded-md bg-red-600 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >{confirmBusy ? 'Видаляю…' : 'Видалити назавжди'}</button>
        </div>
      </div>
    </div>
  {/if}

  {#if createOpen}
    <div class="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onclick={() => (createOpen = false)}>
      <form
        onsubmit={submitCreatePayment}
        onclick={(e) => e.stopPropagation()}
        class="w-full max-w-lg rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div class="flex items-center justify-between border-b border-[var(--color-border-3)] px-5 py-3">
          <h3 class="text-base font-bold">Новий запис</h3>
          <button type="button" onclick={() => (createOpen = false)} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><Icon name="close" size={16} /></button>
        </div>

        <div class="px-5 py-4 grid grid-cols-2 gap-3">
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Сума, ₴ *</div>
            <input bind:value={nAmount} type="number" step="0.01" min="0" required
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Валюта</div>
            <input bind:value={nCurrency}
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Статус *</div>
            <select bind:value={nStatus} class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm">
              <option value="SUCCEEDED">SUCCEEDED</option>
              <option value="REFUNDED">REFUNDED</option>
              <option value="FAILED">FAILED</option>
              <option value="PENDING">PENDING</option>
            </select>
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Дата платежу *</div>
            <input bind:value={nOccurredAt} type="date" required
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Acquirer</div>
            <select bind:value={nProvider} class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm">
              <option value="UNKNOWN">UNKNOWN</option>
              <option value="WAYFORPAY">WayForPay</option>
              <option value="LIQPAY">LiqPay</option>
              <option value="FONDY">Fondy</option>
              <option value="PORTMONE">Portmone</option>
              <option value="STRIPE">Stripe</option>
            </select>
          </label>
          <label class="block">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Order ref</div>
            <input bind:value={nExternalRef} placeholder="WFP-…"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-xs font-mono" />
          </label>
          <label class="block col-span-2">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Email платника *</div>
            <input bind:value={nPayerEmail} type="email" placeholder="ivan@example.com"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block col-span-2">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Телефон платника *</div>
            <input bind:value={nPayerPhone} type="tel" placeholder="+380501234567"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>
          <label class="block col-span-2">
            <div class="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-muted-2)]">Raw tariff (товар)</div>
            <input bind:value={nRawTariff} placeholder="club-monthly"
              class="w-full rounded-md border border-[var(--color-border-3)] px-3 py-2 text-sm" />
          </label>

          <p class="col-span-2 text-[11px] text-[var(--color-text-muted-2)]">
            Потрібен хоча б один з: email або телефон — щоб привʼязати до Person.
            При SUCCEEDED LTV та paymentCount Person'а оновляться автоматично.
          </p>

          {#if createErr}
            <div class="col-span-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700 whitespace-pre-wrap">{createErr}</div>
          {/if}
        </div>

        <div class="flex justify-end gap-2 border-t border-[var(--color-border-3)] px-5 py-3">
          <button
            type="button"
            onclick={() => (createOpen = false)}
            class="rounded-md border border-[var(--color-border-3)] bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]"
          >
            Скасувати
          </button>
          <button
            type="submit"
            disabled={createBusy}
            class="rounded-md bg-[var(--color-pu)] px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
          >
            {createBusy ? 'Створення…' : 'Створити'}
          </button>
        </div>
      </form>
    </div>
  {/if}
</AppShell>

<style>
  /* Вибраний рядок — мʼякий фіолетовий + акцентна планка зліва. */
  tr.selected-row { background: var(--color-pu-light); box-shadow: inset 2px 0 0 var(--color-pu); }
</style>
